import React, { useState, useEffect } from 'react';
import { X, FileVideo, Loader2 } from 'lucide-react';
import '../styles/AttachmentUploader.css';

/**
 * AttachmentThumbnail Component
 *
 * Displays a thumbnail preview of an uploaded image or video
 * with an X button to remove it
 *
 * @param {Object} attachment - Attachment object with url, filename, type, size
 * @param {Function} onRemove - Callback when X button clicked
 * @param {boolean} uploading - Whether file is currently uploading
 * @param {number} uploadProgress - Upload progress 0-100
 */
const AttachmentThumbnail = ({ attachment, onRemove, uploading = false, uploadProgress = 0 }) => {
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const isImage = attachment.type === 'image';
  const isVideo = attachment.type === 'video';

  useEffect(() => {
    // For images, create a thumbnail preview from the URL
    if (isImage && attachment.url) {
      setThumbnailUrl(attachment.url);
    }
  }, [attachment.url, isImage]);

  return (
    <div
      className="attachment-thumbnail"
      style={{
        position: 'relative',
        width: '100px',
        height: '100px'
      }}
    >
      {/* Thumbnail content */}
      <div className="attachment-thumbnail-content">
        {isImage && thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={attachment.filename}
            className="attachment-thumbnail-image"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100px',
              height: '100px',
              maxWidth: '100px',
              maxHeight: '100px',
              minWidth: '100px',
              minHeight: '100px',
              objectFit: 'cover',
              display: 'block'
            }}
          />
        ) : isVideo ? (
          <div className="attachment-thumbnail-video">
            <FileVideo size={32} />
          </div>
        ) : null}

        {/* Upload progress overlay */}
        {uploading && (
          <div className="attachment-thumbnail-uploading">
            <Loader2 className="attachment-upload-spinner" size={32} />
          </div>
        )}
      </div>

      {/* Remove button */}
      {!uploading && (
        <button
          type="button"
          className="attachment-thumbnail-remove"
          onClick={onRemove}
          aria-label={`Remove ${attachment.filename}`}
          style={{
            position: 'absolute',
            top: '4px',
            right: '4px',
            zIndex: 10
          }}
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
};

export default React.memo(AttachmentThumbnail);
